import { useAuth } from "features/auth";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { api } from "utils/api";
import { followKeys } from "./queryKeys";

type FollowsResponse = {
  artistIds: string[];
};

const getFollows = () =>
  api.get<FollowsResponse>("/follow", { requireAuth: true });

/*
 * Get all follows for signed in user
 */
const useFollows = () => {
  const { user } = useAuth();
  return useQuery(followKeys.user(user?.id ?? "loggedOut"), getFollows, {
    enabled: !!user,
  });
};

const getUserFollows = (userId: string) =>
  api.get<FollowsResponse>(`/users/${userId}/follows`);

/*
 * Get all follows for a user by id
 */
const useUserFollows = (userId: string) => {
  const { user } = useAuth();
  return useQuery(followKeys.user(userId), () => getUserFollows(userId), {
    enabled: !!user,
  });
};

type FollowParams = {
  artistId?: string;
  // Returns all artists from a collective
  badge?: string;
  unfollow: boolean;
};

const postUpdateFollow = (params: FollowParams) =>
  api.post("/follow/artist", {
    body: JSON.stringify(params),
  });

/*
 * Follow or Unfollow an Artist
 */
const useFollow = ({
  artistId,
  badge,
}: {
  artistId?: string;
  badge?: string;
}) => {
  const queryClient = useQueryClient();
  const mutation = useMutation(postUpdateFollow, {
    onSuccess: () => {
      queryClient.invalidateQueries(followKeys.base);
      queryClient.invalidateQueries(
        artistId ? ["ARTIST", artistId] : ["ARTIST"]
      );
    },
  });

  // Helpers for UI to Follow or Unfollow

  const follow = () =>
    artistId || badge
      ? mutation.mutate({ artistId, badge, unfollow: false })
      : () => undefined;

  const unfollow = () =>
    artistId || badge
      ? mutation.mutate({ artistId, badge, unfollow: true })
      : () => undefined;

  return {
    follow,
    unfollow,
    ...mutation,
  };
};

export { useFollows, useFollow, useUserFollows };
